import { storageManager } from "@/services/storage/storage/storage";

type EnsureVgImageCachedOptions = {
  fetchFn?: typeof fetch;
};

/**
 * Ensures the VG hole image is cached in IndexedDB keyed by URL.
 * Returns a blob: object URL on success, or null so callers can fall back
 * to the remote URL (e.g. when CORS blocks blob conversion).
 */
export async function ensureVgImageCached(
  imageUrl: string,
  options?: EnsureVgImageCachedOptions
): Promise<string | null> {
  const trimmed = imageUrl.trim();
  if (!trimmed) {
    return null;
  }

  await storageManager.initialize();

  const cached = await storageManager.getCourseImageUrl(trimmed);
  if (cached) {
    await storageManager.deleteCourseImagesExcept(trimmed);
    return cached;
  }

  const fetchFn = options?.fetchFn ?? fetch;

  try {
    const response = await fetchFn(trimmed, {
      mode: "cors",
      credentials: "omit",
    });
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    if (blob.size === 0) {
      return null;
    }

    await storageManager.storeCourseImage(trimmed, blob);
    await storageManager.deleteCourseImagesExcept(trimmed);
    return await storageManager.getCourseImageUrl(trimmed);
  } catch (error) {
    console.error("Failed to cache VG course image:", error);
    return null;
  }
}

export async function warmVgImageCache(
  imageUrl: string | undefined
): Promise<void> {
  if (!imageUrl?.trim()) {
    return;
  }
  await ensureVgImageCached(imageUrl);
}
